import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

const EMBEDDING_WEBHOOK_URL = 'https://maxbconnect.duckdns.org/webhook/f72041c5-cdad-4950-9ab7-ca2a0243ec25';

/**
 * Hook to check if the embedding is stale and update it if necessary.
 * Returns the valid embedding to be used for matching.
 */
export const useEnsureEmbedding = () => {
    const { user } = useAuth();
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState(null);

    const ensureEmbedding = useCallback(async () => {
        if (!user) {
            alert("Veuillez vous connecter.");
            return null;
        }

        setIsUpdating(true);
        setError(null);

        try {
            // 1. Fetch current timestamps and embedding from profile
            const { data: profile, error: fetchError } = await supabase
                .from('profiles')
                .select('embedding, last_cv_data_update, last_match_update, cv_match_update_count')
                .eq('id', user.id)
                .single();

            if (fetchError) throw fetchError;

            // 2. Logic to check if stale
            const lastDataUpdate = profile.last_cv_data_update ? new Date(profile.last_cv_data_update) : null;
            const lastMatchUpdate = profile.last_match_update ? new Date(profile.last_match_update) : null;

            // If no data update ever, we can't do anything (or maybe we can? let's stick to simple logic)
            // If no match update EVER, or data is NEWER than match, we need update.
            const needsUpdate = !profile.embedding || !lastMatchUpdate || (lastDataUpdate && lastDataUpdate > lastMatchUpdate);

            if (needsUpdate) {
                console.log("🔄 Embedding is stale or missing. Triggering update...");

                // 3. Call Webhook to generate new embedding
                const response = await fetch(EMBEDDING_WEBHOOK_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId: user.id })
                });

                if (!response.ok) throw new Error('Erreur lors de la génération du profil IA (Webhook).');

                const webhookData = await response.json();
                console.log("Webhook Response:", webhookData);

                // Handle array response if N8N returns array
                const result = Array.isArray(webhookData) ? webhookData[0] : webhookData;

                if (!result || !result.embedding) {
                    throw new Error("Réponse invalide du serveur de matching.");
                }

                // 4. Update Supabase with new embedding and timestamp
                const updates = {
                    cv_match_update_count: (profile.cv_match_update_count || 0) + 1,
                    last_match_update: result.last_match_update || new Date().toISOString(),
                    embedding: result.embedding
                };

                const { error: updateError } = await supabase
                    .from('profiles')
                    .update(updates)
                    .eq('id', user.id);

                if (updateError) throw updateError;

                console.log("✅ Embedding updated successfully.");
                return result.embedding;

            } else {
                console.log("✅ Embedding is fresh. Using existing.");
                return profile.embedding;
            }

        } catch (err) {
            console.error("Error ensuring embedding:", err);
            setError(err.message || "Erreur lors de la vérification du profil.");
            alert("Une erreur est survenue lors de la mise à jour de votre profil match.");
            return null;
        } finally {
            setIsUpdating(false);
        }
    }, [user]);

    return {
        ensureEmbedding,
        isUpdating,
        error
    };
};

export default useEnsureEmbedding;
